import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, map, Observable, of, Subject, takeUntil, tap } from 'rxjs';

import { NotificationApiMessageCenterApiService } from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import {
	NotificationStoreData,
	MessageCenterNotificationsListType
} from '@shure/cloud/shared/notifications/shared/models/notification-models';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { APP_ENVIRONMENT, AppEnvironment } from '@shure/cloud/shared/utils/config';
import {
	PRISM_DIALOG_DATA,
	PrismButtonComponent,
	PrismDialogComponent,
	PrismDialogRef,
	PrismSnackbarService
} from '@shure/prism-angular-components';
import { getConvertedDateTime, updateDateFormat } from '@shure/shared/angular/ui/utils/dates';

@Component({
	selector: 'sh-notification-details-main',
	templateUrl: './notification-details-main.component.html',
	styleUrl: './notification-details-main.component.scss',
	imports: [
		RouterModule,
		FormsModule,
		ReactiveFormsModule,
		PrismDialogComponent,
		TranslocoModule,
		PrismButtonComponent
	],
	changeDetection: ChangeDetectionStrategy.Default
})
export class NotificationDetailsMainComponent implements OnInit, OnDestroy {
	public environment: AppEnvironment;
	public isListPage = false;
	public isMarkedRead = false;
	public notificationListType = '';
	public notificationData!: NotificationStoreData;
	public notifications$: Observable<NotificationStoreData[] | null> = EMPTY;
	public formatTime;
	public readonly destroy$ = new Subject<void>();
	public timeZone!: string | undefined;
	public displayDateFormat!: string;
	protected readonly messageCenterNotificationsListType = MessageCenterNotificationsListType;

	constructor(
		private dialogRef: PrismDialogRef<NotificationDetailsMainComponent>,
		private notificationService: NotificationApiMessageCenterApiService,
		private notificationsStore: NotificationsStore,
		public snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService,
		private organizationStore: OrganizationsStoreService,
		@Inject(APP_ENVIRONMENT) appEnv: AppEnvironment,
		@Inject(PRISM_DIALOG_DATA)
		public data: {
			notificationData: NotificationStoreData;
			isListPage: boolean;
			shouldMarkAsReadOnOpen: boolean;
			notificationListType: string;
		}
	) {
		this.environment = appEnv;
		this.formatTime = getConvertedDateTime;
	}

	/**
	 * Initializes component and loads notification details
	 */
	public ngOnInit(): void {
		this.notificationData = this.data.notificationData;
		this.notificationListType = this.data.notificationListType;
		const accountInfo = this.organizationStore.accountInformation();
		this.timeZone = accountInfo.preferences?.timeZone;
		this.displayDateFormat = accountInfo.preferences?.dateFormat
			? updateDateFormat(accountInfo.preferences?.dateFormat)
			: updateDateFormat('mm/dd/yyyy');
		this.isListPage = this.data.isListPage;
		if (!this.notificationData.readAt) {
			this.markAsRead(this.notificationData.id);
		}
	}

	/**
	 * Closes the dialog
	 */
	public closePopup(): void {
		this.dialogRef.close();
	}

	//TODO:BSR To be uncommented when we utilise markAsArchive functionality
	/**
	 * Marks a notification as archived
	 * @param selectedId The ID of the notification to archive
	 */
	public markAsArchive(selectedId: string): void {
		const requestData = {
			application: this.environment.application as string,
			body: [
				{
					id: selectedId,
					isArchived: true
				}
			]
		};
		const updatedNotifications = [
			{
				id: selectedId,
				isArchived: true
			}
		];

		this.notificationService
			.postNotifications(requestData)
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.notificationsStore.prependOrPatchNotifications(updatedNotifications);
				this.dialogRef.close();
			});
	}

	/**
	 * Marks a notification as read
	 * @param selectedId The ID of the notification to mark as read
	 */
	public markAsRead(selectedId: string): void {
		const data = {
			id: selectedId,
			isRead: true,
			isTrashed: this.notificationListType === this.messageCenterNotificationsListType.TRASH ? true : false
		};
		const requestData = {
			application: this.environment.application as string,
			body: [data]
		};
		this.notificationService
			.postNotifications(requestData)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.isMarkedRead = true;
				}
			});
	}

	public extractTextFromHtml(item?: string): string {
		return item ? item.replace(/<[^>]*(>|$)| |‌|»|«|>/g, ' ') : '';
	}

	/**
	 * Deletes the notification by closing the dialog
	 */
	public deleteNotification(): void {
		this.dialogRef.close();
	}

	/**
	 * Gets the current date in UTC format
	 * @returns The current date in UTC format as a string
	 */
	public getCurrentDateInUTC(): string {
		const currentDate = new Date(new Date().getTime() + 10000);
		return currentDate.toISOString().slice(0, 19) + 'Z';
	}

	/**
	 * Performs cleanup when the component is destroyed
	 */
	public ngOnDestroy(): void {
		if (this.isMarkedRead) {
			this.notificationsStore.prependOrPatchNotifications([
				{ ...this.notificationData, readAt: this.getCurrentDateInUTC() }
			]);
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	public markAsTrash(selectedId: string): void {
		const data = {
			id: selectedId,
			isRead: true,
			isTrashed: true
		};
		const requestData = {
			application: this.environment.application as string,
			body: [data]
		};

		this.notificationService
			.postNotifications(requestData)
			.pipe(
				takeUntil(this.destroy$),
				tap(() => {
					this.notificationsStore.prependOrPatchNotifications([
						{ ...this.notificationData, trashedAt: this.getCurrentDateInUTC() }
					]);
				}),
				map((): { success: boolean } => ({ success: true })),
				catchError(() => of({ success: false } as { success: boolean })),
				tap((result: { success: boolean }) => {
					this.dialogRef.close(result);
				})
			)
			.subscribe();
	}
}
